import { FormControl, FormField, HeadlessFormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";

import { useAuth } from "@/context/auth/AuthContext";
import { useFieldLabelCustomized } from "@/context/FieldCustomizationOverrideContext";
import { SupportedLocale } from "@/i18n";
import { cn } from "@/lib/utils";
import { QuestionMarkCircledIcon } from "@radix-ui/react-icons";
import { Pencil } from "lucide-react";
import {
  type ComponentProps,
  type ReactNode,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Text } from "./AbstractForm";
import {
  MaskableFieldHeader,
  useMaskableFieldExpanded,
} from "./MaskableFieldHeader";
import type { MaskableFieldProps } from "./MaskedFormFieldShell";
import RequiredAsterisk from "@/components/ui/required-asterisk";
import { ModifiableListContent } from "./ModifiableListContent";

export { ModifiableListContent } from "./ModifiableListContent";

export const ModifiableList = ({
                                 name,
                                 type,
                                 field,
                                 listKey,
                                 append = false,
                                 appendSeparator = " ",
                                 label,
                                 lang,
                                 labelClassName,
                                 inputClassName,
                                 tooltipText,
                                 description,
                                 visible = true,
                                 corner,
                                 nonMaskable,
                                 maskable,
                                 required,
                                 ...props
                               }: ComponentProps<typeof Text> &
  MaskableFieldProps & {
  listKey: string;
  append?: boolean;
  /** When append is true, separator used between values (default: " "). */
  appendSeparator?: string;
  lang?: SupportedLocale;
  labelClassName?: string;
  inputClassName?: string;
  tooltipText?: string;
  /** Texte informatif bleu sous le contrôle. */
  description?: string;
  visible?: boolean;
  required?: boolean;
  corner?: ReactNode | (() => ReactNode);
}) => {
  const form = useFormContext();
  const { t } = useTranslation();
  const labelCustomized = useFieldLabelCustomized(name);
  const { user } = useAuth();
  const isReader = user?.role === "reader" ? true : false;
  const { enabled: maskEnabled, showControl, minimized } =
    useMaskableFieldExpanded(name, {
      nonMaskable,
      maskable,
    });

  const [open, setOpen] = useState(false);
  const cornerComponent =
    typeof corner === "function" ? corner() : corner;

  if (!visible || minimized) {
    return null;
  }

  const labelNode = (
    <div className="flex flex-row items-center gap-2">
      <span className={cn(labelCustomized && "italic")}>
        {label ?? t(name)}
        {required ? (
          <>
            {" "}
            <RequiredAsterisk />
          </>
        ) : null}
      </span>
      {tooltipText && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              className="bg-transparent"
              onClick={(e) => e.preventDefault()}
            >
              <QuestionMarkCircledIcon className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="top" className="text-center text-black">
              <p>{tooltipText}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );

  const legacyLabel = (
    <div className="mb-1.5 flex w-full flex-row items-center justify-between gap-2">
      <div className="flex flex-row items-center gap-2">
        <label
          className={cn(
            "flex text-start  text-slate-700 text-sm font-medium font-['Raleway'] leading-normal",
            labelCustomized && "italic",
            labelClassName,
          )}
          htmlFor={name}
        >
          {label ?? t(name)}
          {required ? (
            <>
              {" "}
              <RequiredAsterisk />
            </>
          ) : null}
        </label>
        {tooltipText && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger
                className="bg-transparent"
                onClick={(e) => e.preventDefault()}
              >
                <QuestionMarkCircledIcon className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="text-center text-black">
                <p>{tooltipText}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {cornerComponent != null && <div>{cornerComponent}</div>}
    </div>
  );

  return (
    <div className="flex w-full flex-col">
      {maskEnabled && !nonMaskable ? (
        <MaskableFieldHeader
          fieldName={name}
          maskable={maskable}
          className="mb-1.5 w-full"
          label={labelNode}
          labelClassName={labelClassName}
          trailing={cornerComponent ?? undefined}
        />
      ) : (
        legacyLabel
      )}
      <FormField
        defaultValue=""
        name={name}
        {...form}
        {...field}
        render={({ field }) => (
          <div
            className={cn(
              "flex w-full flex-col",
              maskEnabled && !showControl && "hidden",
            )}
          >
            <div className="flex flex-row gap-4 w-full">
              <HeadlessFormItem>
                <FormControl>
                  <Input
                    id={name}
                    type={type ?? "text"}
                    {...field}
                    {...props}
                    className={cn(inputClassName)}
                  />
                </FormControl>
              </HeadlessFormItem>
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger disabled={isReader}>
                  <Pencil
                    className={cn(
                      "size-4",
                      isReader && "opacity-50 cursor-default",
                    )}
                  />
                </SheetTrigger>

                <SheetContent className="w-[500px] px-0 max-h-lvh">
                  <ModifiableListContent
                    name={typeof label === "string" ? label : t(name)}
                    listKey={listKey}
                    onSelectValue={(value) => {
                      const newValue = append
                        ? field.value
                          ? `${field.value}${appendSeparator}${value}`
                          : value
                        : value;

                      field.onChange(newValue);
                      setOpen(false);
                    }}
                    lang={lang}
                  />
                </SheetContent>
              </Sheet>
            </div>
            {description ? (
              <p className="mt-1.5 text-primary text-[12px] font-normal font-['Raleway'] leading-normal">
                {description}
              </p>
            ) : null}
          </div>
        )}
      />
    </div>
  );
};
